import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LogService } from '../util/log.service';
import { AuthenticationService } from './authentication.service';

/** A Stay reggeli-rendelések útvonala — nem permission-alapú, ezért nincs a protectedRoutes között. */
const STAY_ORDERS_ROUTE = 'stay-orders';

/**
 * Service for managing route access based on user permissions
 */
@Injectable({
  providedIn: 'root'
})
export class RouteAccessService {
  // Store protected routes that require specific permissions, with their path and required permissions
  private protectedRoutes: { path: string; requiredPermissions: string[] }[] = [];
  private routesInitialized = false;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly router: Router,
    private readonly log: LogService
  ) {}

  /**
   * Initialize the list of protected routes from the application routes configuration
   * This is called lazy to avoid circular dependencies
   */
  private initializeProtectedRoutesIfNeeded(): void {
    if (this.routesInitialized) {
      return;
    }

    const appRoutes = this.router.config;
    // Find the AppLayout routes where the protected routes are defined
    const mainLayoutRoutes = appRoutes.find((route) => route.path === '' && route.component && route.children);

    if (mainLayoutRoutes && mainLayoutRoutes.children) {
      // Extract paths and required permissions
      mainLayoutRoutes.children.forEach((route) => {
        if (route.path && route.path !== '' && route.canActivate && route.data && route.data['requiredPermissions']) {
          this.protectedRoutes.push({
            path: route.path,
            requiredPermissions: route.data['requiredPermissions']
          });
        }
      });
    }

    this.routesInitialized = true;
    this.log.debug('Protected routes initialized:', this.protectedRoutes);
  }

  /**
   * Get the first accessible route for the current user
   * @returns The path of the first accessible route, or null if no routes are accessible
   */
  getFirstAccessibleRoute(): string | null {
    this.initializeProtectedRoutesIfNeeded();
    const userPermissions = this.authService.getUserPermissions();
    this.log.debug('User permissions:', userPermissions);

    // First check routes with cleaning permission (priority)
    const cleaningRoutes = this.protectedRoutes.filter((route) => route.requiredPermissions.includes('cleaning'));

    for (const route of cleaningRoutes) {
      if (this.canUserAccessRoute(route.requiredPermissions, userPermissions)) {
        this.log.debug(`First accessible route found: ${route.path}`);
        return route.path;
      }
    }

    // Then check routes with arrival-departures permission
    const arrivalRoutes = this.protectedRoutes.filter((route) => route.requiredPermissions.includes('arrival-departures'));

    for (const route of arrivalRoutes) {
      if (this.canUserAccessRoute(route.requiredPermissions, userPermissions)) {
        this.log.debug(`First accessible route found: ${route.path}`);
        return route.path;
      }
    }

    // Then check all other routes
    for (const route of this.protectedRoutes) {
      if (this.canUserAccessRoute(route.requiredPermissions, userPermissions)) {
        this.log.debug(`First accessible route found: ${route.path}`);
        return route.path;
      }
    }

    // A Stay-munkatársnak egyetlen permission-alapú route-ja sincs (a ROLE_ADMIN_API nem ad
    // jogosultságot), ezért a szerepkör-halmaz alapú Stay-nézet a végső mentsvár — enélkül
    // bejelentkezés után a "nincs jogosultság" oldalra futna.
    if (this.authService.canAccessStayOrders()) {
      this.log.debug(`First accessible route found: ${STAY_ORDERS_ROUTE}`);
      return STAY_ORDERS_ROUTE;
    }

    this.log.warn('No accessible routes found for user');
    return null;
  }

  /**
   * Check if a user has access to a specific route based on required permissions
   * @param requiredPermissions Permissions required to access the route
   * @param userPermissions Permissions the user has
   * @returns True if the user can access the route, false otherwise
   */
  private canUserAccessRoute(requiredPermissions: string[], userPermissions: string[]): boolean {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    const userPermissionSet = new Set(userPermissions);
    return requiredPermissions.every((permission) => userPermissionSet.has(permission));
  }

  /**
   * Check if a user has any accessible routes
   * @returns True if the user has at least one accessible route, false otherwise
   */
  hasAccessibleRoutes(): boolean {
    return this.getFirstAccessibleRoute() !== null;
  }
}
